import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { post, get, put } from '../utility/fetch';
import notification from '../utility/notification';
import logo from "../Assets/images/ConnectHealthPro.png";
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import SignupStepper from './SignupStepper';
import { UserContext } from '../contexts/UserContext';
import { requestForToken } from '../utility/firebase';

const styles = {
    container: {
        display: 'flex',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '24px',
        boxSizing: 'border-box',
    },
    paper: {
        maxWidth: '500px',
        width: '100%',
        margin: 'auto',
        padding: '32px',
        borderRadius: '8px',
        boxShadow: '0px 3px 6px rgba(0,0,0,0.16)',
        backgroundColor: '#fff',
    },
    logoContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '32px',
    },
    logo: {
        height: '60px',
    },
    title: {
        textAlign: 'center',
        marginBottom: '12px',
        color: '#3c7e2d',
        fontSize: '28px',
        fontWeight: 'bold',
    },
    subtitle: {
        textAlign: 'center',
        marginBottom: '32px',
        color: '#666',
        fontSize: '14px',
    },
    formControl: {
        marginBottom: '20px',
        width: '100%',
        position: 'relative',
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#333',
    },
    inputContainer: {
        position: 'relative',
        width: '100%',
    },
    input: {
        width: '100%',
        padding: '12px',
        paddingRight: '48px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '16px',
        boxSizing: 'border-box',
    },
    eyeIcon: {
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        cursor: 'pointer',
        color: '#666',
    },
    button: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#3c7e2d',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        fontSize: '16px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.3s',
        marginTop: '12px',
    },
    buttonDisabled: {
        backgroundColor: '#ccc',
        cursor: 'not-allowed',
    },
    backButton: {
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#3c7e2d',
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        fontSize: '16px',
    },
    checksContainer: {
        marginTop: '16px',
        padding: '16px',
        backgroundColor: '#f9f9f9',
        borderRadius: '4px',
    },
    checkItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '8px',
        fontSize: '13px',
        color: '#666',
    },
    checkItemValid: {
        color: '#3c7e2d',
    },
    checkbox: {
        width: '16px',
        height: '16px',
        marginRight: '8px',
        accentColor: '#3c7e2d',
    },
    required: {
        color: 'red',
    },
    iconContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '24px',
        fontSize: '64px',
    }
};

export default function PasswordSetup() {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const userId = location.state?.userId;
    const { setUserId, setProfilePicture } = useContext(UserContext);

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    // Encouraging message
    const encouragingMessage = {
        title: "Final Step!",
        subtitle: "Create a strong password to protect your health data"
    };

    useEffect(() => {
        if (!email) {
            notification({
                message: "Session expired. Please sign up again.",
                type: "error"
            });
            navigate('/create-account');
        }
    }, [email, navigate]);

    const passwordChecks = {
        hasUppercase: /[A-Z]/.test(formData.password),
        hasLowercase: /[a-z]/.test(formData.password),
        hasNumber: /\d/.test(formData.password),
        hasSpecialChar: /[$#!*()?@&%^]/.test(formData.password),
        minLength: formData.password.length >= 8,
    };

    const isPasswordValid = Object.values(passwordChecks).every(Boolean);
    const passwordsMatch = formData.password && formData.password === formData.confirmPassword;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Function to fetch employee/patient data
    const getEmployeeData = async (userID) => {
        try {
            const response = await get(`/Patient/single/${userID}`);
            if (response?.code === 1) {
                // Store profile picture URL in session storage
                setProfilePicture(response?.data?.pictureUrl);
                sessionStorage.setItem('profilePicture', response.data.pictureUrl);
                // Store other useful data
                sessionStorage.setItem('email', response.data.email);
                sessionStorage.setItem('title', response.data.title);
                sessionStorage.setItem('gender', response.data.gender);
                sessionStorage.setItem('phoneNumber', response.data.phoneNumber);

                // Store healthcare provider info if needed
                if (response.data.healthCareProvider) {
                    sessionStorage.setItem('providerName', response.data.healthCareProvider.name);
                    sessionStorage.setItem('providerLocation', response.data.healthCareProvider.location);
                }

                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to fetch employee data:', error);
            notification({
                type: 'error',
                message: 'Failed to fetch employee data'
            });
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isPasswordValid) {
            notification({
                message: "Please meet all password requirements",
                type: "error"
            });
            return;
        }

        if (!passwordsMatch) {
            notification({
                message: "Passwords do not match",
                type: "error"
            });
            return;
        }

        setLoading(true);

        try {
            const res = await put(`/employee/finalize-onboarding/${email}`, {
                password: formData.password,
                profilePicture: null // No profile picture in this simplified flow
            });

            console.log("Password creation response:", res);

            if (res?.statusCode === 200) {
                notification({
                    message: "Registration complete!",
                    type: "success"
                });
                // Redirect to login page or welcome page
                navigate('/login');
            } else {
                notification({
                    message: res?.message || "Error setting password",
                    type: "error"
                });
            }
        } catch (error) {
            console.error('Password creation error:', error);
            notification({
                message: "Error setting password",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.paper}>
                

                <div style={styles.logoContainer}>
                    <img src={logo} alt="ConnectHealthPro" style={styles.logo} />
                </div>

                <SignupStepper currentStep={3} />



                <h1 style={styles.title}>{encouragingMessage.title}</h1>
                <p style={styles.subtitle}>
                    {encouragingMessage.subtitle}
                </p>

                <form onSubmit={handleSubmit}>
                    <div style={styles.formControl}>
                        <label style={styles.label}>
                            Password <span style={styles.required}>*</span>
                        </label>
                        <div style={styles.inputContainer}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="Enter your password"
                                required
                            />
                            <div
                                style={styles.eyeIcon}
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </div>
                        </div>
                    </div>

                    <div style={styles.checksContainer}>
                        <div style={{
                            ...styles.checkItem,
                            ...(passwordChecks.minLength ? styles.checkItemValid : {})
                        }}>
                            <input
                                type="checkbox"
                                checked={passwordChecks.minLength}
                                readOnly
                                style={styles.checkbox}
                            />
                            At least 8 characters
                        </div>
                        <div style={{
                            ...styles.checkItem,
                            ...(passwordChecks.hasUppercase ? styles.checkItemValid : {})
                        }}>
                            <input
                                type="checkbox"
                                checked={passwordChecks.hasUppercase}
                                readOnly
                                style={styles.checkbox}
                            />
                            Contains uppercase letter (A-Z)
                        </div>
                        <div style={{
                            ...styles.checkItem,
                            ...(passwordChecks.hasLowercase ? styles.checkItemValid : {})
                        }}>
                            <input
                                type="checkbox"
                                checked={passwordChecks.hasLowercase}
                                readOnly
                                style={styles.checkbox}
                            />
                            Contains lowercase letter (a-z)
                        </div>
                        <div style={{
                            ...styles.checkItem,
                            ...(passwordChecks.hasNumber ? styles.checkItemValid : {})
                        }}>
                            <input
                                type="checkbox"
                                checked={passwordChecks.hasNumber}
                                readOnly
                                style={styles.checkbox}
                            />
                            Contains number (0-9)
                        </div>
                        <div style={{
                            ...styles.checkItem,
                            ...(passwordChecks.hasSpecialChar ? styles.checkItemValid : {})
                        }}>
                            <input
                                type="checkbox"
                                checked={passwordChecks.hasSpecialChar}
                                readOnly
                                style={styles.checkbox}
                            />
                            Contains special character ($#!*@&%^?)
                        </div>
                    </div>

                    <div style={styles.formControl}>
                        <label style={styles.label}>
                            Confirm Password <span style={styles.required}>*</span>
                        </label>
                        <div style={styles.inputContainer}>
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                style={{
                                    ...styles.input,
                                    borderColor: formData.confirmPassword && !passwordsMatch ? 'red' : '#ccc'
                                }}
                                placeholder="Re-enter your password"
                                required
                            />
                            <div
                                style={styles.eyeIcon}
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </div>
                        </div>
                        {formData.confirmPassword && !passwordsMatch && (
                            <p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                Passwords do not match
                            </p>
                        )}
                    </div>

                    <button
                        type="submit"
                        style={{
                            ...styles.button,
                            ...(!isPasswordValid || !passwordsMatch || loading ? styles.buttonDisabled : {})
                        }}
                        disabled={!isPasswordValid || !passwordsMatch || loading}
                    >
                        {loading ? 'Creating Password...' : 'Create Password'}
                    </button>
                </form>

                <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '14px', color: '#666' }}>
                    Already have an account?{' '}
                    <span
                        style={{ color: '#3c7e2d', cursor: 'pointer', fontWeight: 'bold' }}
                        onClick={() => navigate('/login')}
                    >
                        Login
                    </span>
                </p>
            </div>
        </div>
    );
}
